/**
 * 后端 API 客户端（骨架占位）。
 *
 * 骨架阶段不发真实请求；此处提供统一的 fetch 封装：自动带 X-Request-Id、
 * 解析后端统一错误结构 {code,message,request_id,details}（doc 01 §12）。
 * 后续用 OpenAPI Generator 生成的 Client/DTO 逐步替换手写调用。
 */

export const API_BASE = process.env.NEXT_PUBLIC_API_BASE ?? "/api/v1";

/**
 * 默认请求超时（毫秒）。后端冷启动（首次无 AI 缓存）最坏≈LLM 超时 15s，故前端取 20s
 * 留余量、不误杀正常冷请求；仅在真正挂起时中止，避免无限等待（doc 05A §10.3 降级思想）。
 */
const DEFAULT_TIMEOUT_MS = 20_000;

export interface ApiErrorBody {
  code: string;
  message: string;
  request_id: string;
  details?: Record<string, unknown>;
}

export class ApiError extends Error {
  readonly code: string;
  readonly requestId: string;
  readonly status: number;
  readonly details?: Record<string, unknown>;

  constructor(status: number, body: ApiErrorBody) {
    super(body.message);
    this.name = "ApiError";
    this.status = status;
    this.code = body.code;
    this.requestId = body.request_id;
    this.details = body.details;
  }
}

function genRequestId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `req-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

/** 统一 JSON 请求封装。带超时与调用方信号中止，避免后端挂起时无限等待。 */
export async function fetchJson<T>(
  path: string,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set("X-Request-Id", genRequestId());
  if (!headers.has("Content-Type") && init.body && !(init.body instanceof FormData)) {
    headers.set("Content-Type", "application/json");
  }

  // 超时与调用方 AbortSignal（如 TanStack 卸载/重取）任一触发即中止请求。
  const controller = new AbortController();
  const external = init.signal;
  const onAbort = () => controller.abort();
  if (external) {
    if (external.aborted) {
      controller.abort();
    } else {
      external.addEventListener("abort", onAbort, { once: true });
    }
  }
  const timer = timeoutMs > 0 ? setTimeout(() => controller.abort(), timeoutMs) : null;

  try {
    const res = await fetch(`${API_BASE}${path}`, {
      ...init,
      headers,
      // 默认同源经 Next 反代到后端；include 兼容本地直连 API_BASE 时的登录 Cookie。
      credentials: "include",
      signal: controller.signal,
    });

    if (!res.ok) {
      let body: ApiErrorBody;
      try {
        body = (await res.json()) as ApiErrorBody;
      } catch {
        body = {
          code: "UNKNOWN",
          message: res.statusText,
          request_id: headers.get("X-Request-Id") ?? "",
        };
      }

      // 只有真正的登录态失效才跳登录页。部分受保护业务端点也可能返回 401
      // （如 server-side token 缺失），这些应展示业务错误，不能把用户踢回登录。
      if (
        res.status === 401 &&
        body.code === "UNAUTHENTICATED" &&
        typeof window !== "undefined"
      ) {
        const here = window.location.pathname + window.location.search;
        if (!window.location.pathname.startsWith("/login")) {
          window.location.assign(`/login?redirect=${encodeURIComponent(here)}`);
        }
      }
      throw new ApiError(res.status, body);
    }

    // 204 或空响应直接返回，避免 JSON.parse 抛错（如登出）。
    if (res.status === 204) {
      return undefined as T;
    }
    const text = await res.text();
    return (text ? JSON.parse(text) : undefined) as T;
  } finally {
    if (timer) clearTimeout(timer);
    if (external) external.removeEventListener("abort", onAbort);
  }
}
